"use client"

import { Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { JobPostingStatus } from "@/gql/graphql"
import { TextVariant } from "@/theme"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import * as Tooltip from "@radix-ui/react-tooltip"
import { formatDate, formatDistanceStrict } from "date-fns"
import { Action, actionProgressPercentage, determineJobPostingAction } from "../../utilts"
import { CoverImageWithEdit } from "../CoverImageWithEdit"
import adjustDateForTimezone from "@/utils/dates"
import React, { useState } from "react"
import clsx from "clsx"

const MyJobsJobPostMetadataFragment = graphql(`
  fragment MyJobsJobPostMetadata on JobPosting {
    __typename
    id
    title
    views
    createdAt
    status
    applicationCount
    closedAt
    ...CoverImageWithEdit
    ...JobTitleColorised
    ...MyJobsJobPostStatusDeterminator
  }
`)

interface Props {
  jobPosting: FragmentType<typeof MyJobsJobPostMetadataFragment> | null | undefined
  titleSize?: TextVariant
  showStatus?: boolean
  bottomRightElement?: () => React.ReactNode
  onEdit?: () => void
  onClickTitle?: () => void
  eagerImage: boolean
}

export const MyJobsJobPostMetadata: React.FC<Props> = ({
  titleSize = "body1",
  showStatus = true,
  bottomRightElement,
  onEdit,
  onClickTitle,
  eagerImage,
  ...props
}) => {
  const jobPosting = useFragment(MyJobsJobPostMetadataFragment, props.jobPosting)

  const jobStatus = determineJobPostingAction(jobPosting)

  return (
    <div className="hidden min-h-[115px] w-full flex-row items-start gap-x-6 md:flex">
      <CoverImageWithEdit jobPosting={jobPosting} />

      <div className="flex h-full w-full flex-col justify-between pb-[1px] pt-[2px]">
        <div className="flex flex-col gap-y-2">
          <div onClick={onClickTitle} className={clsx(!!onClickTitle && "cursor-pointer")}>
            <Text variant={titleSize}>{jobPosting?.title}</Text>
          </div>

          <div className="flex flex-row flex-wrap items-center gap-x-2">
            <Text variant="label" className="text-fg-2">
              {formatDistanceStrict(
                jobPosting?.createdAt
                  ? adjustDateForTimezone(jobPosting.createdAt)!
                  : adjustDateForTimezone(new Date().toISOString())!,
                adjustDateForTimezone(new Date().toISOString())!,
                {
                  addSuffix: true,
                }
              )}
            </Text>
            <div className="size-1 rounded-full bg-fg-2" />

            <Text variant="label" className="text-fg-2">
              {jobPosting?.views} views
            </Text>
            <div className="size-1 rounded-full bg-fg-2" />

            <Text variant="label" className="text-fg-2">
              {jobPosting?.applicationCount} applications
            </Text>
          </div>
        </div>

        <div className="mt-3 flex w-full flex-row items-end justify-between">
          {showStatus &&
            (jobPosting?.status === JobPostingStatus.Published ? (
              <div className="flex flex-col gap-y-3">
                <div className="flex flex-row items-end gap-x-[6px]">
                  <Text variant="label" className="text-fg-2">
                    Next
                  </Text>
                  <Text variant="label" className="text-fg-1">
                    {jobStatus}
                  </Text>
                  <Tooltip.Provider delayDuration={300} skipDelayDuration={500}>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <InformationCircleIcon className="stroke-fg-3" width={16} height={16} name="info" />
                      </Tooltip.Trigger>
                      <Tooltip.Content
                        style={{
                          width: statusToTooltip[jobStatus].width,
                          boxShadow: "0px 4px 24px 0px rgba(0, 0, 0, 0.10)",
                        }}
                        className="rounded-[6px] bg-bg-1 p-4">
                        <Text variant="label">{statusToTooltip[jobStatus].content}</Text>
                        <Tooltip.Arrow className="fill-bg-1" />
                      </Tooltip.Content>
                    </Tooltip.Root>
                  </Tooltip.Provider>
                </div>

                <div className="h-1 w-[200px] rounded-full bg-bg-3 lg:w-[274px]">
                  <div
                    style={{ width: actionProgressPercentage(jobStatus) }}
                    className="h-full rounded-full bg-primary-3"></div>
                </div>
              </div>
            ) : jobPosting?.status === JobPostingStatus.Closed && jobPosting?.closedAt ? (
              <div className="flex flex-col gap-y-3">
                <Text variant="label" className="text-fg-2">
                  Closed {formatDate(jobPosting?.closedAt, "MMM d, yyyy")}
                </Text>
              </div>
            ) : (
              <div />
            ))}
          <div>{bottomRightElement?.()}</div>
        </div>
      </div>
    </div>
  )
}

const statusToTooltip: Record<Action, { content: string; width: string }> = {
  [Action.SEND_INVITE]: { content: "Invite creatives to apply to your job", width: "253px" },
  [Action.REVIEW_APPLICATIONS]: {
    content: `Navigate to your unreviewed applications and mark them as “interested” or “passed”`,
    width: "269px",
  },
  [Action.MESSAGE_APPLICANTS]: {
    content: "Chat with prospective creatives to confirm interest and availability",
    width: "253px",
  },
  //[Action.HIRE]: { content: "Report who you hired for your internal records", width: "191px" },
  [Action.CLOSE_JOB]: {
    content: `Close your job to remove it 
  from the active section`,
    width: "205px",
  },
  [Action.CLOSED]: {
    content: `This job is closed`,
    width: "205px",
  },
}
